import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Descriptions, Tag, Button, Space, Typography, Spin, message, Empty, Alert } from 'antd';
import { ArrowLeftOutlined, WalletOutlined, DollarOutlined, CopyOutlined, ReloadOutlined, WarningOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  error: '#EF4444',
  warning: '#F59E0B',
};

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};

  /* Fix Alert text colors */
  .ant-alert {
    color: ${COLORS.textPrimary} !important;
    
    .ant-alert-message {
      color: ${COLORS.textPrimary} !important;
    }
    
    .ant-alert-description {
      color: ${COLORS.textSecondary} !important;
      
      p, ul, li {
        color: ${COLORS.textSecondary} !important;
      }
    }
  }

  /* Fix Empty component text */
  .ant-empty {
    .ant-empty-description {
      color: ${COLORS.textSecondary} !important;
    }
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const StyledCard = styled(Card)`
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  margin-bottom: 24px;
  
  .ant-card-head {
    border-bottom: 1px solid ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-card-head-title {
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-card-body {
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-descriptions-item-label {
    background: ${COLORS.bgPrimary} !important;
    color: ${COLORS.textSecondary} !important;
    border-color: ${COLORS.border} !important;
  }
  
  .ant-descriptions-item-content {
    background: ${COLORS.bgSecondary} !important;
    color: ${COLORS.textPrimary} !important;
    border-color: ${COLORS.border} !important;
  }
`;

const InternalWallet: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [companyData, setCompanyData] = useState<any>(null);
  const [internalWallet, setInternalWallet] = useState<any>(null);
  const [walletBalances, setWalletBalances] = useState<any>(null);
  const [loadingInternalWallet, setLoadingInternalWallet] = useState(false);
  const [hasInternalWalletId, setHasInternalWalletId] = useState<boolean | null>(null);

  const fetchCompanyData = async () => {
    if (!companyId) return;

    try {
      const companyResponse = await apiClient.get(
        API_ENDPOINTS.ADMIN_GET_COMPANY_DETAILS.replace('{companyId}', companyId)
      );

      if (companyResponse.data?.status === 'success' && companyResponse.data?.data) {
        const data = companyResponse.data.data;
        setCompanyData(data);
        const hasInternalWallet = !!data.internalWalletId;
        setHasInternalWalletId(hasInternalWallet);
        return hasInternalWallet;
      }
    } catch (companyError: any) {
      console.error('Error fetching company details:', companyError);
      message.error('Failed to load company information');
    }
    return false;
  };

  const fetchInternalWallet = async () => {
    if (!companyId) return;

    try {
      setLoadingInternalWallet(true);
      const endpoint = API_ENDPOINTS.ADMIN_GET_INTERNAL_WALLET.replace('{companyId}', companyId);
      const response = await apiClient.get(endpoint);

      if (response.data?.status === 'success' && response.data?.data) {
        setInternalWallet(response.data.data.wallet);
        setWalletBalances(response.data.data.balances);
      }
    } catch (error: any) {
      console.error('Error fetching internal wallet:', error);
      message.error(error.response?.data?.message || 'Failed to load internal wallet');
    } finally {
      setLoadingInternalWallet(false);
    }
  };

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    message.success(`${label} copied to clipboard`);
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const hasWallet = await fetchCompanyData();
      if (hasWallet) {
        await fetchInternalWallet();
      }
      setLoading(false);
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  if (loading) {
    return (
      <PageContainer>
        <Spin size="large" style={{ display: 'block', textAlign: 'center', padding: '50px' }} />
      </PageContainer>
    );
  }

  if (hasInternalWalletId === false) {
    return (
      <PageContainer>
        <HeaderContainer>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(`/kivu/companies/${companyId}`)}
            style={{
              background: COLORS.bgSecondary,
              borderColor: COLORS.border,
              color: COLORS.textPrimary,
            }}
          >
            Back to Company
          </Button>
        </HeaderContainer>
        <Empty
          description="Internal wallet not assigned to this merchant company"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </PageContainer>
    );
  }

  if (!internalWallet) {
    return (
      <PageContainer>
        <HeaderContainer>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(`/kivu/companies/${companyId}`)}
            style={{
              background: COLORS.bgSecondary,
              borderColor: COLORS.border,
              color: COLORS.textPrimary,
            }}
          >
            Back to Company
          </Button>
        </HeaderContainer>
        <Spin size="large" style={{ display: 'block', textAlign: 'center', padding: '50px' }} />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <HeaderContainer>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(`/kivu/companies/${companyId}`)}
          style={{
            background: COLORS.bgSecondary,
            borderColor: COLORS.border,
            color: COLORS.textPrimary,
          }}
        >
          Back to Company
        </Button>
        <Button
          icon={<ReloadOutlined />}
          onClick={fetchInternalWallet}
          loading={loadingInternalWallet}
          style={{
            background: COLORS.bgSecondary,
            borderColor: COLORS.border,
            color: COLORS.textPrimary,
          }}
        >
          Refresh
        </Button>
      </HeaderContainer>

      {companyData && (
        <Alert
          message={`Internal Wallet for ${companyData.name}`}
          description={`This is the internal wallet used for billing and funding operations.`}
          type="info"
          showIcon
          style={{
            background: COLORS.bgSecondary,
            borderColor: COLORS.border,
            marginBottom: 24,
          }}
        />
      )}

      <StyledCard
        title={
          <Space>
            <WalletOutlined />
            <span>Internal Wallet Details</span>
          </Space>
        }
      >
        <Descriptions column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }} bordered>
          <Descriptions.Item label="Wallet ID">
            <Space>
              <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                {internalWallet.walletId || internalWallet.id}
              </span>
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={() => handleCopy(internalWallet.walletId || internalWallet.id, 'Wallet ID')}
                style={{ color: COLORS.accent }}
              />
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Member ID">{internalWallet.externalMemberId || '-'}</Descriptions.Item>
          <Descriptions.Item label="Member Name">{internalWallet.externalMemberName || '-'}</Descriptions.Item>
          <Descriptions.Item label="Member Email">{internalWallet.externalMemberEmail || '-'}</Descriptions.Item>
          {internalWallet.evmAddress && (
            <Descriptions.Item label="EVM Address">
              <Space>
                <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                  {internalWallet.evmAddress}
                </span>
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => handleCopy(internalWallet.evmAddress, 'EVM Address')}
                  style={{ color: COLORS.accent }}
                />
              </Space>
            </Descriptions.Item>
          )}
          {internalWallet.solanaAddress && (
            <Descriptions.Item label="Solana Address">
              <Space>
                <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                  {internalWallet.solanaAddress}
                </span>
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => handleCopy(internalWallet.solanaAddress, 'Solana Address')}
                  style={{ color: COLORS.accent }}
                />
              </Space>
            </Descriptions.Item>
          )}
          {internalWallet.bitcoinAddress && (
            <Descriptions.Item label="Bitcoin Address">
              <Space>
                <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                  {internalWallet.bitcoinAddress}
                </span>
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => handleCopy(internalWallet.bitcoinAddress, 'Bitcoin Address')}
                  style={{ color: COLORS.accent }}
                />
              </Space>
            </Descriptions.Item>
          )}
        </Descriptions>
      </StyledCard>

      {walletBalances && walletBalances.chains && walletBalances.chains.length > 0 && (
        <StyledCard
          title={
            <Space>
              <DollarOutlined />
              <span>Wallet Balances</span>
            </Space>
          }
        >
          {walletBalances.chains.map((chain: any, index: number) => (
            <Card
              key={index}
              style={{
                background: COLORS.bgPrimary,
                border: `1px solid ${COLORS.border}`,
                marginBottom: 16,
              }}
            >
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography.Title level={5} style={{ color: COLORS.textPrimary, margin: 0 }}>
                    {chain.chainName}
                  </Typography.Title>
                  {chain.isLowBalance && (
                    <Tag color="warning" icon={<WarningOutlined />}>
                      Low Balance
                    </Tag>
                  )}
                </div>
                {chain.balances && chain.balances.length > 0 ? (
                  chain.balances.map((balance: any, balanceIndex: number) => (
                    <div
                      key={balanceIndex}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '8px 0',
                        borderBottom: `1px solid ${COLORS.border}`,
                      }}
                    >
                      <div>
                        <div style={{ color: COLORS.textPrimary, fontWeight: 500 }}>
                          {balance.currencyName} ({balance.currencySymbol})
                        </div>
                        <div style={{ color: COLORS.textSecondary, fontSize: '12px' }}>
                          {balance.blockchainNetworkName}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ color: COLORS.textPrimary, fontFamily: 'monospace' }}>
                          {Number(balance.balance || 0).toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 8,
                          })}{' '}
                          {balance.currencySymbol}
                        </div>
                        <div style={{ color: COLORS.textSecondary, fontSize: '12px' }}>
                          ${Number(balance.balanceUsd || 0).toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ color: COLORS.textSecondary, textAlign: 'center', padding: '16px' }}>
                    No balances found for this chain
                  </div>
                )}
              </Space>
            </Card>
          ))}
        </StyledCard>
      )}

      <Alert
        message="About Internal Wallets"
        description={
          <div style={{ color: COLORS.textSecondary }}>
            <p style={{ color: COLORS.textSecondary, marginBottom: 8 }}>
              The internal wallet is used for billing and funding operations. Merchants can send funds to this wallet address, which will be automatically detected and converted to USD for their internal wallet balance.
            </p>
            <p style={{ marginTop: 8, marginBottom: 0, color: COLORS.textSecondary }}>
              <InfoCircleOutlined style={{ marginRight: 4 }} />
              Funds sent to this wallet are automatically converted to USD and added to the merchant's internal wallet balance for invoice payments.
            </p>
          </div>
        }
        type="info"
        showIcon
        style={{ background: COLORS.bgSecondary, borderColor: COLORS.border }}
      />
    </PageContainer>
  );
};

export default InternalWallet;
